import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { apiClient } from '@/lib/api/client';
import { beginLogin, endSessionUrl, isConfigured, refreshTokens, type TokenSet } from './keycloak';
import { clearAccessToken, getAccessToken, setAccessToken } from './token-store';

export type UserRole = 'admin' | 'editor' | 'viewer';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  role: UserRole;
  /** Admin-controlled: user has been granted permission to opt into beta features. */
  beta_allowed: boolean;
  /** User-controlled: user has opted into beta features. */
  beta_features_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True when Keycloak is not configured — login is impossible, say it. */
  isAuthUnavailable: boolean;
  login: () => void;
  logout: () => void;
  /** Called by the callback route once the code has been exchanged. */
  onTokensAcquired: (tokens: TokenSet) => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  isAdmin: boolean;
  /** True iff the user has been granted beta access AND has opted in. */
  canUseBeta: boolean;
  /** Re-fetch the current user profile (e.g. after toggling a flag). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Renew this many ms BEFORE the access token expires. A realm token lives 300 s
 * (measured), so 45 s of margin leaves room for a slow round-trip and a laptop
 * that just woke up, without renewing more than once a minute.
 */
const RENEW_MARGIN_MS = 45_000;
/** Never schedule a timer shorter than this (avoids a refresh storm). */
const MIN_RENEW_DELAY_MS = 5_000;
/**
 * Marks that the one silent (prompt=none) attempt of this tab has been spent.
 * Without it, a realm that answers `login_required` would send the app back to
 * Keycloak on every mount: an infinite redirect loop, the classic failure of
 * this pattern. Cleared by an explicit login, kept by an explicit logout so a
 * signed-out user stays signed out.
 */
const SILENT_ATTEMPT_KEY = 'snxt_onto_sso_attempted';

/**
 * Where the user actually wants to go.
 *
 * Normally that is the current location. On `/auth` it is the `returnTo` the callback put there after a
 * silent attempt failed: without this, the interactive login records `returnTo=/auth`, the callback
 * refuses to send anyone back into the auth routes, and the deep link becomes `/`. That was the bug
 * measured on 2026-08-19, and it is why the current path alone is not enough.
 *
 * The search string is kept: a link to a filtered view must come back filtered.
 */
export function intendedRoute(): string {
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname !== '/auth') return here;
  const wanted = new URLSearchParams(window.location.search).get('returnTo');
  if (!wanted) return '/';
  // Same rule as the callback: in-app paths only, never back into /auth.
  if (!wanted.startsWith('/') || wanted.startsWith('//') || wanted.startsWith('/auth')) return '/';
  return wanted;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [hasToken, setHasToken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * The refresh token stays in a ref: same reason as the access token, it never
   * goes to storage, and it must not re-render anything when it rotates.
   */
  const refreshTokenRef = useRef<string | null>(null);
  const renewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSession = useCallback(() => {
    if (renewTimerRef.current) clearTimeout(renewTimerRef.current);
    renewTimerRef.current = null;
    refreshTokenRef.current = null;
    clearAccessToken();
    setHasToken(false);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async (): Promise<void> => {
    // apiClient attaches the in-memory bearer itself.
    const userData = await apiClient.get<AuthUser>('/api/auth/me');
    setUser(userData);
  }, []);

  /**
   * Indirection through a ref: `applyTokens` arms a timer that calls `renew`,
   * and `renew` calls `applyTokens` back. The ref breaks the cycle without
   * lying to the dependency linter.
   */
  const renewRef = useRef<() => Promise<void>>(async () => {});

  /** Install a token set: memory, refresh ref, and the renewal timer. */
  const applyTokens = useCallback((tokens: TokenSet): void => {
    setAccessToken(tokens.accessToken);
    refreshTokenRef.current = tokens.refreshToken;
    setHasToken(true);
    if (renewTimerRef.current) clearTimeout(renewTimerRef.current);
    if (tokens.refreshToken) {
      const delay = Math.max(tokens.expiresAt - Date.now() - RENEW_MARGIN_MS, MIN_RENEW_DELAY_MS);
      renewTimerRef.current = setTimeout(() => void renewRef.current(), delay);
    }
  }, []);

  /** Refresh, then re-arm the timer. A failure ends the session (no loop). */
  const renew = useCallback(async (): Promise<void> => {
    const refreshToken = refreshTokenRef.current;
    if (!refreshToken) {
      clearSession();
      return;
    }
    try {
      applyTokens(await refreshTokens(refreshToken));
    } catch (error) {
      console.warn('[auth] silent renewal failed, signing out:', error);
      clearSession();
    }
  }, [clearSession, applyTokens]);

  useEffect(() => {
    renewRef.current = renew;
  }, [renew]);

  const onTokensAcquired = useCallback(
    async (tokens: TokenSet): Promise<void> => {
      applyTokens(tokens);
      // RE-ARM the one silent attempt, now that we know it works.
      //
      // The flag exists so a browser whose SSO session is dead does not bounce to Keycloak forever. It
      // was set before the attempt and never cleared, so it also blocked the SECOND page load in a tab:
      // tokens live in memory only, a reload starts with none, the boot effect saw the flag and gave up,
      // and the guard showed the sign-in wall. Measured 2026-08-19 on the deployed app — pressing F5
      // looked like being logged out, and it broke three e2e specs whose second half is exactly "reload
      // and check it persisted". Clearing it on SUCCESS keeps the loop guard (a failing attempt leaves
      // the flag set) while making a reload free, which is what the comment on the effect below always
      // claimed.
      sessionStorage.removeItem(SILENT_ATTEMPT_KEY);
      setIsLoading(true);
      try {
        await fetchUser();
      } catch (error) {
        console.error('[auth] /api/auth/me rejected the fresh token:', error);
        clearSession();
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUser, clearSession, applyTokens]
  );

  // Clean the timer up on unmount so a hot reload does not leak refreshes.
  useEffect(() => {
    return () => {
      if (renewTimerRef.current) clearTimeout(renewTimerRef.current);
    };
  }, []);

  const login = useCallback(() => {
    sessionStorage.removeItem(SILENT_ATTEMPT_KEY);
    void beginLogin(intendedRoute());
  }, []);

  /**
   * On boot, try ONCE to pick the session back up without a click: nothing is
   * persisted, so a reload (or a fresh e2e page) has no token, but the Keycloak
   * SSO cookie usually still stands and prompt=none turns that into a code
   * without showing a form. If it fails, the user lands on /auth and clicks.
   */
  useEffect(() => {
    if (!isConfigured()) return;
    if (getAccessToken()) return;
    if (window.location.pathname === '/auth/callback') return;
    if (sessionStorage.getItem(SILENT_ATTEMPT_KEY)) return;
    sessionStorage.setItem(SILENT_ATTEMPT_KEY, '1');
    setIsLoading(true);
    // `intendedRoute()` and NOT the raw location: by the time this runs the guard may already have
    // replaced the URL with `/auth?returnTo=/editor`, and recording THAT as the route to come back to
    // is what kept the deep link broken through three earlier fixes. Measured transaction before the
    // fix: `returnTo: "/auth?returnTo=%2Feditor"`, which the callback refuses, so "/".
    void beginLogin(intendedRoute(), { silent: true });
  }, []);

  const logout = useCallback(() => {
    clearSession();
    // Log out of Keycloak too: keeping the SSO cookie would sign the user back
    // in silently on the next click, which is not what "log out" means.
    if (isConfigured()) {
      window.location.assign(endSessionUrl());
    }
  }, [clearSession]);

  const hasRole = useCallback(
    (role: UserRole): boolean => {
      if (!user) return false;
      const hierarchy: Record<UserRole, number> = { admin: 3, editor: 2, viewer: 1 };
      return hierarchy[user.role] >= hierarchy[role];
    },
    [user]
  );

  /**
   * Refresh the user from /api/auth/me. Used after the user (or an admin)
   * mutates a flag on the user profile (e.g. beta_features_enabled) so the
   * UI immediately reflects the new state without forcing a full reload.
   */
  const refreshUser = useCallback(async () => {
    if (!getAccessToken()) return;
    await fetchUser();
  }, [fetchUser]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && hasToken,
    isLoading,
    isAuthUnavailable: !isConfigured(),
    login,
    logout,
    onTokensAcquired,
    hasRole,
    isAdmin: user?.role === 'admin',
    canUseBeta: !!user && user.beta_allowed === true && user.beta_features_enabled === true,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Made with Bob
